/**
 * Backoff and circuit-breaker for error-logging (and similar) requests.
 * Ported from cb-hp-ui to avoid flooding when the log endpoint fails.
 */

/**
 * @typedef {Object} BackoffConfig
 * @property {number} maxConsecutiveFailures
 * @property {number} baseDelayMs
 * @property {number} maxDelayMs
 * @property {number} rateLimitWindowMs
 * @property {number} maxRequestsPerWindow
 * @property {number} [timeoutMs]
 * @property {string} [identifier]
 */

/**
 * @typedef {Object} BackoffState
 * @property {number} consecutiveFailures
 * @property {number} lastFailureTime
 * @property {boolean} isDisabled
 * @property {number} activeRequests
 * @property {Array<{ timestamp: number }>} requestQueue
 */

const backoffStates = new Map();

function getState(identifier) {
  if (!backoffStates.has(identifier)) {
    backoffStates.set(identifier, {
      consecutiveFailures: 0,
      lastFailureTime: 0,
      isDisabled: false,
      activeRequests: 0,
      requestQueue: [],
    });
  }
  return backoffStates.get(identifier);
}

function calculateBackoffDelay(failureCount, baseDelayMs, maxDelayMs) {
  return Math.min(baseDelayMs * Math.pow(2, failureCount - 1), maxDelayMs);
}

export function resetBackoffState(identifier) {
  const state = getState(identifier);
  state.consecutiveFailures = 0;
  state.lastFailureTime = 0;
  state.isDisabled = false;
}

export function clearAllBackoffStates() {
  backoffStates.clear();
}

function attemptCircuitBreakerRecovery(config, state) {
  if (!state.isDisabled) return false;
  const now = Date.now();
  const backoffMs = calculateBackoffDelay(config.maxConsecutiveFailures, config.baseDelayMs, config.maxDelayMs);
  if (now - state.lastFailureTime >= backoffMs) {
    resetBackoffState(config.identifier || 'default');
    return true;
  }
  return false;
}

function recordFailure(config, state, onCircuitBreakerOpen) {
  state.consecutiveFailures++;
  state.lastFailureTime = Date.now();
  if (state.consecutiveFailures >= config.maxConsecutiveFailures) {
    state.isDisabled = true;
    if (onCircuitBreakerOpen) onCircuitBreakerOpen();
  }
}

function checkRateLimit(config, state) {
  const now = Date.now();
  state.requestQueue = state.requestQueue.filter((entry) => now - entry.timestamp < config.rateLimitWindowMs);
  if (state.requestQueue.length >= config.maxRequestsPerWindow) return false;
  state.requestQueue.push({ timestamp: now });
  return true;
}

/**
 * @param {{ config: BackoffConfig, url?: string, skipUrlCheck?: (url: string) => boolean }} options
 * @returns {boolean}
 */
export function shouldSkipRequest(options) {
  const { config, url = '', skipUrlCheck } = options;
  const identifier = config.identifier || 'default';
  const state = getState(identifier);

  if (!attemptCircuitBreakerRecovery(config, state) && state.isDisabled) return true;
  if (url && skipUrlCheck && skipUrlCheck(url)) return true;

  const now = Date.now();
  if (state.consecutiveFailures > 0) {
    const backoffMs = calculateBackoffDelay(state.consecutiveFailures, config.baseDelayMs, config.maxDelayMs);
    if (now - state.lastFailureTime < backoffMs) return true;
  }
  return false;
}

/**
 * @param {{ config: BackoffConfig, requestFn: (signal?: AbortSignal) => Promise<any>, onSuccess?: () => void, onFailure?: () => void, onCircuitBreakerOpen?: () => void }} options
 * @returns {Promise<void>}
 */
export async function executeWithBackoff(options) {
  const { config, requestFn, onSuccess, onFailure, onCircuitBreakerOpen } = options;
  const identifier = config.identifier || 'default';
  const state = getState(identifier);

  if (state.isDisabled && !attemptCircuitBreakerRecovery(config, state)) {
    return Promise.resolve();
  }
  if (!checkRateLimit(config, state)) return Promise.resolve();

  state.activeRequests++;

  let controller;
  let timeoutId;
  if (config.timeoutMs) {
    controller = new AbortController();
    timeoutId = setTimeout(() => controller.abort(), config.timeoutMs);
  }

  try {
    await requestFn(controller?.signal);
    resetBackoffState(identifier);
    if (onSuccess) onSuccess();
  } catch (error) {
    recordFailure(config, state, onCircuitBreakerOpen);
    if (onFailure) onFailure();
  } finally {
    if (timeoutId) clearTimeout(timeoutId);
    state.activeRequests = Math.max(0, state.activeRequests - 1);
  }
}
